import { toaster } from '@/components/ui/toast'
// apps/app/src/state/recordings/state.ts
import { api } from '@api'
import { userId } from '@state/auth'
import { useMutation, useQuery } from '@tanstack/solid-query'
import { queryClient } from 'src/App'
import { deleteRecording } from './api'

export function useRecordings() {
  const discoveryQuery = useQuery(() => ({
    queryKey: ['recordings-groups', userId()],
    queryFn: async () => {
      const { data, error } = await api().rpc('get_recording_discovery_rows')
      if (error) throw error
      return data.sort(
        (a, b) =>
          new Date(b.latest_activity).getTime() - new Date(a.latest_activity).getTime(),
      )
    },
    enabled: !!userId(),
  }))

  const latestRecordingsQuery = useQuery(() => ({
    queryKey: ['latest-recordings', userId()],
    queryFn: async () => {
      const { data, error } = await api()
        .from('recordings')
        .select('*')
        .order('created_at', { ascending: false }) // Change to 'occurrence_date' if you prefer sorting by event time
        .limit(3)

      if (error) throw error
      return data
    },
    enabled: !!userId(),
  }))

  const deleteMutation = useMutation(() => ({
    mutationFn: deleteRecording,
    onSuccess: () => {
      toaster.create({
        title: 'Recording deleted',
        type: 'success',
      })
      // Invalidate both the discovery rows and the specific group lists
      queryClient.invalidateQueries({ queryKey: ['recordings-groups', userId()] })
      queryClient.invalidateQueries({ queryKey: ['recordings', userId()] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Error deleting recording',
        description: error.message,
        type: 'error',
      })
    },
  }))

  return {
    discoveryQuery,
    latestRecordingsQuery,
    discoveryRows: () => discoveryQuery.data ?? [],
    deleteRecording: (id: string) => deleteMutation.mutate(id),
    isDeleting: deleteMutation.isPending,
    status: () => {
      if (discoveryQuery.isPending) return 'LOADING'
      if (discoveryQuery.isError) return 'ERROR'
      if (!discoveryQuery.data?.length) return 'EMPTY'
      return 'SUCCESS'
    },
  }
}
